import { reactive } from 'vue';
import { accessLevelApi } from '../services/accessLevelApi';
import {
  getAccessLevelLadder,
  getAccessLevelOptions,
  getAccessLevelOptionsIncluding,
  getAccessLevels,
  setAccessLevelLadder,
} from './accessLevel';

/**
 * Reactive store ของขั้นบันได Access Level (#914) ที่ dropdown / badge ทั้งแอปอ่านร่วมกัน
 * (Flow builder inspector ×8, DebugControlBar, `/Stream`, badge บน canvas ใน FlowNode).
 *
 * - เริ่มจาก**ค่าในตัว T0–T3** (`utils/accessLevel.js`) ⇒ ยังไม่โหลด / โหลดพลาด / ยังไม่รัน SQL = พฤติกรรมเดิม
 * - `ensureAccessLevelsLoaded()` โหลด `GET api/v1/AccessLevel` ครั้งเดียวต่อหน้า (promise dedupe —
 *   หลาย component mount พร้อมกันยิงครั้งเดียว) · สำเร็จ ⇒ `setAccessLevelLadder()` + อัป state + `version++`
 *   · พลาด ⇒ คงค่าเดิม + เก็บข้อความใน `error` (**ไม่เด้ง popup** — ใช้ promise service ไม่ใช่ `$call`)
 *   แล้วเรียกครั้งถัดไปจะลองใหม่
 * - `refreshAccessLevels()` บังคับโหลดใหม่ (หน้า `/AccessLevelConfig` เรียกหลังเพิ่ม/ลบ/เรียงลำดับ)
 * - promise ที่คืนจากทั้งสองตัว **ไม่ reject** — `await` ได้โดยไม่ต้อง catch
 *
 * 🔴 consumer ต้องอ่านผ่าน computed / template ที่แตะ `accessLevelState` (ห้ามจับค่าใน `data()`)
 *    ไม่งั้นจะได้ค่าในตัว ณ ตอนสร้าง component และไม่เห็นระดับใหม่หลังโหลด
 *
 * @typedef {Object} AccessLevelState
 * @property {ReadonlyArray<{code: string, nameTh: string, nameEn: string}>} levels ladder ปัจจุบัน (ต่ำ → สูง)
 * @property {ReadonlyArray<string>} codes code ของ ladder ปัจจุบัน
 * @property {ReadonlyArray<{title: string, value: string}>} options ตัวเลือก dropdown
 * @property {boolean} loaded โหลดจาก server สำเร็จแล้วอย่างน้อยหนึ่งครั้ง
 * @property {boolean} loading กำลังโหลด
 * @property {?string} error ข้อความ error ของการโหลดครั้งล่าสุด (null = ไม่มี)
 * @property {boolean} fromDatabase server อ่านจากตาราง `cus.AccessLevel` (false = ค่าในตัว / ยังไม่รัน SQL)
 * @property {?string} warning ข้อความเตือนจาก server (เช่น อ่านตารางไม่ได้)
 * @property {number} version เพิ่มทุกครั้งที่ ladder ถูกตั้งจาก server (ใช้เป็น dependency บังคับ re-render)
 */

/** @type {AccessLevelState} */
export const accessLevelState = reactive({
  levels: getAccessLevelLadder(),
  codes: getAccessLevels(),
  options: getAccessLevelOptions(),
  loaded: false,
  loading: false,
  error: null,
  fromDatabase: false,
  warning: null,
  version: 0,
});

/** promise ของการโหลดที่ยังไม่จบ (dedupe) */
let pending = null;
/** ลำดับคำขอ — ผลของคำขอที่เก่ากว่าล่าสุดถูกทิ้ง (กัน refresh ซ้อนกันตอบกลับสลับลำดับ) */
let requestSeq = 0;

function syncFromLadder() {
  accessLevelState.levels = getAccessLevelLadder();
  accessLevelState.codes = getAccessLevels();
  accessLevelState.options = getAccessLevelOptions();
}

function toMessage(err) {
  return (err && (err.message || String(err))) || 'โหลดระดับ Access Level ไม่สำเร็จ';
}

/**
 * นำผล `AccessLevelLadderDto` (`{ levels: [{code,nameTh,nameEn,…}], fromDatabase, warning }`) มาตั้ง ladder
 * @returns {boolean} true เมื่อ ladder ถูกตั้ง
 */
function applyLadder(data) {
  const rows = data && (data.levels ?? data.Levels);
  const applied =
    Array.isArray(rows) &&
    setAccessLevelLadder(
      rows.map((row) =>
        row && typeof row === 'object'
          ? { code: row.code ?? row.Code, nameTh: row.nameTh ?? row.NameTh, nameEn: row.nameEn ?? row.NameEn }
          : row
      )
    );
  if (!applied) {
    return false;
  }
  syncFromLadder();
  accessLevelState.fromDatabase = Boolean(data.fromDatabase ?? data.FromDatabase);
  accessLevelState.warning = (data.warning ?? data.Warning) || null;
  accessLevelState.error = null;
  accessLevelState.loaded = true;
  accessLevelState.version += 1;
  return true;
}

function fetchLadder() {
  const mySeq = ++requestSeq;
  accessLevelState.loading = true;
  const promise = (async () => {
    try {
      const data = await accessLevelApi.list();
      if (mySeq === requestSeq && !applyLadder(data)) {
        accessLevelState.error = 'รายการระดับจาก server ว่างหรือรูปแบบไม่ถูกต้อง — ใช้ค่าเดิมต่อ';
      }
    } catch (err) {
      if (mySeq === requestSeq) {
        accessLevelState.error = toMessage(err);
      }
    } finally {
      if (mySeq === requestSeq) {
        accessLevelState.loading = false;
        pending = null;
      }
    }
    return accessLevelState;
  })();
  pending = promise;
  return promise;
}

/**
 * โหลด ladder จาก server ถ้ายังไม่เคยโหลดสำเร็จ (เรียกใน `created()`/`mounted()` ของทุก consumer ได้เลย)
 * @returns {Promise<AccessLevelState>} ไม่ reject
 */
export function ensureAccessLevelsLoaded() {
  if (accessLevelState.loaded) {
    return Promise.resolve(accessLevelState);
  }
  return pending || fetchLadder();
}

/**
 * บังคับโหลด ladder ใหม่ (หลังแก้ขั้นบันไดในหน้า `/AccessLevelConfig`)
 * @returns {Promise<AccessLevelState>} ไม่ reject
 */
export function refreshAccessLevels() {
  return fetchLadder();
}

/**
 * ตัวเลือก dropdown ของ ladder ปัจจุบัน + แถว `{ title: "<raw> (ไม่รู้จัก — ระบบถือเป็นระดับสูงสุด)", value: raw, unknown: true }`
 * เมื่อ `raw` ไม่ว่างและไม่อยู่ใน ladder · ใช้ใน template ได้ตรง ๆ (`:items="accessLevelOptionsIncluding(output.accessLevel)"`)
 * — แตะ `accessLevelState` ทุกครั้งจึง re-render เองเมื่อ ladder โหลดเสร็จ
 * @param {unknown} raw ค่า accessLevel ที่ตั้งบน intent
 */
export function accessLevelOptionsIncluding(raw) {
  // dependency ของ reactive render — ladder ใน accessLevel.js เป็น module state ธรรมดา
  void accessLevelState.version;
  void accessLevelState.options;
  return getAccessLevelOptionsIncluding(raw);
}

export default accessLevelState;
